/**
 * Session tracker client.
 * Talks to the local backend under /api/session to:
 *  - record every login (with local device_id, browser IP, user agent)
 *  - enforce single-active-session per user across devices
 *  - heartbeat periodically so we detect being "kicked" by a login elsewhere
 *
 * auth, session_id and device_id live in shared browser storage so every tab
 * uses the same authenticated user and tracked backend session.
 */
import axios from 'axios';
import { baseURL } from '@/config/APIConfig';

const API = `${baseURL.AiBaseURL}/api/session`;
const LS_DEVICE_ID = 'shipez.device_id';
const LS_SESSION_ID = 'shipez.session_id';
const LS_DEVICE_NAME = 'shipez.device_name';

// ── Device identity ────────────────────────────────────────────────────
const uuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

export const getDeviceId = () => {
    let id = localStorage.getItem(LS_DEVICE_ID);
    if (!id) {
        id = uuid();
        localStorage.setItem(LS_DEVICE_ID, id);
    }
    return id;
};

export const getDeviceName = () => {
    let name = localStorage.getItem(LS_DEVICE_NAME);
    if (name) return name;
    const ua = navigator.userAgent;
    let os = 'Unknown';
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/Macintosh|Mac OS X/.test(ua)) os = 'macOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad/.test(ua)) os = 'iOS';
    else if (/Linux/.test(ua)) os = 'Linux';
    let br = 'Browser';
    if (/Edg\//.test(ua)) br = 'Edge';
    else if (/Chrome\//.test(ua)) br = 'Chrome';
    else if (/Firefox\//.test(ua)) br = 'Firefox';
    else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) br = 'Safari';
    name = `${br} on ${os}`;
    localStorage.setItem(LS_DEVICE_NAME, name);
    return name;
};

// ── Local IP via WebRTC (best-effort; may fail in some browsers) ───────
const detectLocalIp = () =>
    new Promise((resolve) => {
        try {
            const RTCPeer = window.RTCPeerConnection || window.webkitRTCPeerConnection;
            if (!RTCPeer) return resolve(null);
            const pc = new RTCPeer({ iceServers: [] });
            let found = null;
            pc.createDataChannel('');
            pc.onicecandidate = (e) => {
                if (!e.candidate) {
                    try { pc.close(); } catch (err) { /* ignore */ }
                    resolve(found);
                    return;
                }
                const m = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9:]+)/i.exec(e.candidate.candidate);
                if (m && !found && !m[0].endsWith('.0') && !m[0].startsWith('0.')) {
                    found = m[0];
                }
            };
            pc.createOffer().then((off) => pc.setLocalDescription(off)).catch(() => resolve(null));
            setTimeout(() => {
                try { pc.close(); } catch (err) { /* ignore */ }
                resolve(found);
            }, 900);
        } catch (err) {
            resolve(null);
        }
    });

/** Stable user id for session tracking — must match on login and heartbeat. */
export const getSessionUserId = (user) =>
    String(
        user?.userID ??
        user?.userId ??
        user?.USERID ??
        user?.UserId ??
        user?.id ??
        user?.email ??
        'unknown',
    );

// ── API surface ────────────────────────────────────────────────────────
export const sessionLogin = async ({ user, force = false }) => {
    const local_ip = await detectLocalIp();
    const { data } = await axios.post(`${API}/login`, {
        user_id: getSessionUserId(user),
        user_name: user.userName || user.fullName || user.name,
        email: user.email,
        device_id: getDeviceId(),
        device_name: getDeviceName(),
        local_ip,
        user_agent: navigator.userAgent,
        force,
    });
    if (data.success && data.session_id) {
        localStorage.setItem(LS_SESSION_ID, data.session_id);
    }
    return data;
};

export const sessionHeartbeat = async (userId) => {
    const session_id = localStorage.getItem(LS_SESSION_ID);
    if (!session_id) return { valid: false, kicked: false };
    const { data } = await axios.post(`${API}/heartbeat`, {
        user_id: String(userId),
        session_id,
    });
    return data;
};

export const sessionLogout = async (userId) => {
    const session_id = localStorage.getItem(LS_SESSION_ID);
    if (!session_id) return { success: true };
    try {
        await axios.post(
            `${API}/logout`,
            { user_id: String(userId), session_id },
            { timeout: 3000 },
        );
    } catch (e) { /* ignore */ }
    localStorage.removeItem(LS_SESSION_ID);
    return { success: true };
};

export const sessionRecordFailed = async ({ email, reason }) => {
    try {
        const local_ip = await detectLocalIp();
        await axios.post(`${API}/failed-login`, {
            email, reason,
            device_id: getDeviceId(),
            local_ip,
            user_agent: navigator.userAgent,
        });
    } catch (e) { /* ignore */ }
};

export const clearSessionId = () => localStorage.removeItem(LS_SESSION_ID);

/**
 * Wipe local storage on sign-out but keep the device identity.
 *
 * The backend recognises a returning browser by `device_id` and resumes its
 * session instead of treating it as a second device. Losing the id turns the
 * next sign-in into a false "already signed in on another device" conflict.
 */
export const clearStorageKeepingDevice = () => {
    const keep = [LS_DEVICE_ID, LS_DEVICE_NAME].map((key) => [
        key,
        localStorage.getItem(key),
    ]);
    localStorage.clear();
    for (const [key, value] of keep) {
        if (value != null) localStorage.setItem(key, value);
    }
};
export const getSessionId = () => localStorage.getItem(LS_SESSION_ID);
